import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2, CircleHelp, Sparkles } from 'lucide-react';
import { updateSocialMetadata, upsertMeta } from '../lib/seoMetadata';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const safeCtaUrl = (value) => {
  const url = String(value || '').trim();
  if (url.startsWith('/') && !url.startsWith('//') && !url.includes('\\')) return url;
  try {
    const parsed = new URL(url);
    return ['http:', 'https:'].includes(parsed.protocol) ? url : '';
  } catch {
    return '';
  }
};

const CtaLink = ({ href, children, className }) => {
  const safeHref = safeCtaUrl(href);
  if (!safeHref) return null;
  const external = /^https?:\/\//i.test(safeHref);
  return (
    <a
      href={safeHref}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className={className}
    >
      {children}
      <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
    </a>
  );
};

export const LandingPageLayout = ({ page, heroImageOverride = '' }) => {
  const content = page?.content || {};
  const assets = page?.assets || [];
  const heroAsset = assets.find((asset) => asset.id === content.hero_asset_id);
  const heroImage = heroImageOverride || heroAsset?.url;
  const supportingAssets = assets.filter((asset) => asset.id !== content.hero_asset_id);
  const listSections = [
    { title: 'What can begin to shift', items: content.benefits },
    { title: 'What’s included', items: content.included },
  ].filter((section) => Array.isArray(section.items) && section.items.length > 0);

  return (
    <div className="bg-[#fbf9fc] text-[#21172b]">
      <section className="relative overflow-hidden bg-[#100819] pb-20 pt-32 text-white sm:pb-24">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(120,68,153,0.42),_transparent_52%)]" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-[1.1fr_0.9fr] lg:px-12">
          <div className="max-w-3xl">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-yellow-200/30 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-yellow-200">
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              The Wisdom Network
            </div>
            <h1 className="font-title text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-6xl">
              {content.headline || page?.title}
            </h1>
            {content.subheadline && (
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-purple-100 sm:text-xl">
                {content.subheadline}
              </p>
            )}
            {content.intro && (
              <p className="mt-5 max-w-2xl leading-relaxed text-white/75">{content.intro}</p>
            )}
            {safeCtaUrl(content.cta_url) && (
              <CtaLink
                href={content.cta_url}
                className="mt-8 inline-flex min-h-14 items-center justify-center rounded-xl bg-gradient-to-r from-yellow-300 to-amber-400 px-7 py-4 font-bold text-[#21172b] shadow-lg shadow-amber-950/30 transition hover:-translate-y-0.5 hover:from-yellow-200 hover:to-amber-300"
              >
                {content.cta_text || 'Explore the offer'}
              </CtaLink>
            )}
            <p className="mt-5 text-sm text-white/55">
              A thoughtful next step, at your own pace.
            </p>
          </div>
          {heroImage ? (
            <div className="mx-auto w-full max-w-xl">
              <div className="overflow-hidden rounded-[2rem] border border-white/15 bg-white/5 p-2 shadow-2xl shadow-black/40">
                <img
                  src={heroImage}
                  alt={heroAsset?.alt_text || page?.title || 'Offer image'}
                  className="max-h-[540px] w-full rounded-[1.5rem] object-cover"
                  fetchPriority="high"
                  decoding="async"
                />
              </div>
            </div>
          ) : (
            <div className="hidden lg:block" aria-hidden="true">
              <div className="mx-auto flex aspect-[4/3] max-w-xl items-center justify-center rounded-[2rem] border border-white/10 bg-white/[0.035]">
                <Sparkles className="h-16 w-16 text-yellow-200/40" />
              </div>
            </div>
          )}
        </div>
      </section>

      {content.problem_copy && (
        <section className="px-5 py-16 sm:px-8 sm:py-20">
          <div className="mx-auto max-w-4xl">
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-purple-700">
              You’re not alone in this
            </p>
            <h2 className="font-title text-3xl font-bold text-purple-950 sm:text-4xl">
              {content.problem_heading || 'When you’re ready for a different kind of support'}
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-purple-950/75">{content.problem_copy}</p>
            {content.pain_points?.length > 0 && (
              <ul className="mt-7 grid gap-3 sm:grid-cols-2">
                {content.pain_points.map((item, index) => (
                  <li key={`${index}-${item}`} className="flex gap-3 rounded-xl bg-white p-4 shadow-sm ring-1 ring-purple-100">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-purple-600" aria-hidden="true" />
                    <span className="leading-relaxed text-purple-950/80">{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}

      {content.transformation_copy && (
        <section className="bg-purple-950 px-5 py-16 text-white sm:px-8 sm:py-20">
          <div className="mx-auto max-w-5xl text-center">
            <h2 className="font-title text-3xl font-bold sm:text-4xl">
              {content.transformation_heading || 'Make room for a new perspective'}
            </h2>
            <p className="mx-auto mt-6 max-w-3xl text-lg leading-relaxed text-purple-100">
              {content.transformation_copy}
            </p>
          </div>
        </section>
      )}

      {listSections.length > 0 && (
        <section className="px-5 py-16 sm:px-8 sm:py-20">
          <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-2">
            {listSections.map((section) => (
              <div key={section.title} className="rounded-2xl bg-white p-7 shadow-sm ring-1 ring-purple-100 sm:p-9">
                <h2 className="font-title text-2xl font-bold text-purple-950">{section.title}</h2>
                <ul className="mt-6 space-y-4">
                  {section.items.map((item, index) => (
                    <li key={`${index}-${item}`} className="flex gap-3 leading-relaxed text-purple-950/80">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" aria-hidden="true" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}

      {content.offer_copy && (
        <section id="offer-details" className="bg-[#f0eaf5] px-5 py-16 sm:px-8 sm:py-20">
          <div className="mx-auto max-w-4xl">
            <h2 className="font-title text-3xl font-bold text-purple-950 sm:text-4xl">
              {content.offer_heading || 'The offer'}
            </h2>
            <p className="mt-6 whitespace-pre-line text-lg leading-relaxed text-purple-950/80">
              {content.offer_copy}
            </p>
            {content.steps?.length > 0 && (
              <ol className="mt-8 grid gap-4 md:grid-cols-3">
                {content.steps.map((step, index) => (
                  <li key={`${index}-${step}`} className="rounded-xl bg-white p-5 shadow-sm">
                    <span className="mb-3 flex h-9 w-9 items-center justify-center rounded-full bg-purple-100 font-bold text-purple-800">
                      {index + 1}
                    </span>
                    <p className="leading-relaxed text-purple-950/80">{step}</p>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </section>
      )}

      {supportingAssets.length > 0 && (
        <section className="px-5 py-12 sm:px-8">
          <div className="mx-auto grid max-w-6xl gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {supportingAssets.map((asset) => (
              <img
                key={asset.id}
                src={asset.url}
                alt={asset.alt_text || ''}
                loading="lazy"
                decoding="async"
                className="max-h-[440px] w-full rounded-2xl object-cover shadow-md"
              />
            ))}
          </div>
        </section>
      )}

      {content.faqs?.length > 0 && (
        <section className="px-5 py-16 sm:px-8 sm:py-20">
          <div className="mx-auto max-w-4xl">
            <div className="mb-8 flex items-center gap-3">
              <CircleHelp className="h-7 w-7 text-purple-700" aria-hidden="true" />
              <h2 className="font-title text-3xl font-bold text-purple-950">Questions you may have</h2>
            </div>
            <div className="space-y-3">
              {content.faqs.map((faq, index) => (
                <details key={`${index}-${faq.question}`} className="group rounded-xl bg-white p-5 shadow-sm ring-1 ring-purple-100">
                  <summary className="cursor-pointer list-none font-semibold text-purple-950">
                    {faq.question}
                  </summary>
                  <p className="mt-3 leading-relaxed text-purple-950/75">{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {(content.closing_heading || content.closing_copy) && (
        <section className="bg-gradient-to-br from-[#170d21] via-purple-950 to-[#2d1742] px-5 py-16 text-center text-white sm:px-8 sm:py-20">
          <div className="mx-auto max-w-3xl">
            <h2 className="font-title text-3xl font-bold sm:text-4xl">
              {content.closing_heading || 'Take the next step when you’re ready'}
            </h2>
            {content.closing_copy && (
              <p className="mt-5 text-lg leading-relaxed text-purple-100">{content.closing_copy}</p>
            )}
            {safeCtaUrl(content.cta_url) && (
              <CtaLink
                href={content.cta_url}
                className="mt-8 inline-flex min-h-14 items-center justify-center rounded-xl bg-gradient-to-r from-yellow-300 to-amber-400 px-7 py-4 font-bold text-[#21172b] shadow-lg transition hover:-translate-y-0.5"
              >
                {content.cta_text || 'Explore the offer'}
              </CtaLink>
            )}
            {content.disclaimer && (
              <p className="mx-auto mt-6 max-w-2xl text-xs leading-relaxed text-white/55">{content.disclaimer}</p>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

const PublicLandingPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setPage(null);
    setNotFound(false);
    fetch(`${API_BASE}/api/landing-pages/${encodeURIComponent(slug)}`)
      .then(async (response) => {
        if (response.status === 404) return null;
        if (!response.ok) throw new Error('Could not load this page');
        return response.json();
      })
      .then((result) => {
        if (!active) return;
        if (!result) {
          setNotFound(true);
          return;
        }
        setPage(result);
        const description = String(result.content?.subheadline || result.content?.intro || result.title)
          .replace(/\s+/g, ' ')
          .trim()
          .slice(0, 300);
        const title = `${result.title} | The Wisdom Network`;
        document.title = title;
        upsertMeta('meta[name="description"]', { name: 'description' }, description);
        updateSocialMetadata({ title, description, url: window.location.href });
      })
      .catch((error) => {
        console.error('Could not load landing page:', error);
        if (active) setNotFound(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [slug]);

  if (loading) {
    return <div className="min-h-[60vh] bg-[#fbf9fc] pt-32 text-center text-purple-900">Loading offer…</div>;
  }
  if (notFound || !page) {
    return (
      <div className="min-h-[60vh] bg-[#fbf9fc] px-6 pt-36 text-center text-purple-950">
        <h1 className="font-title text-3xl font-bold">This offer isn’t available</h1>
        <p className="mt-3 text-purple-800/75">The page may be unpublished or the link may have changed.</p>
        <Link to="/" className="mt-6 inline-block font-semibold text-purple-700 underline">Return to the home page</Link>
      </div>
    );
  }
  return <LandingPageLayout page={page} />;
};

export default PublicLandingPage;
